import { memberId } from '@trello/session-cookie';
import { ProductFeatures } from '@trello/product-features';
// eslint-disable-next-line no-restricted-imports
import { Board_Prefs_PermissionLevel } from '@trello/graphql/generated';
import { useCanDeleteBoardQuery } from './CanDeleteBoardQuery.generated';
import { useCanDeleteBoardOrganizationQuery } from './CanDeleteBoardOrganizationQuery.generated';
import { useCanDeleteBoardEnterpriseQuery } from './CanDeleteBoardEnterpriseQuery.generated';

export const useCanDeleteBoard = (boardId: string) => {
  const { data: boardData, loading: loadingBoardData } = useCanDeleteBoardQuery(
    {
      variables: { boardId },
    },
  );

  const {
    data: orgData,
    loading: loadingOrgData,
  } = useCanDeleteBoardOrganizationQuery({
    variables: { orgId: boardData?.board?.idOrganization || '' },
    skip: !boardData?.board?.idOrganization,
  });

  // Enterprise data only necessary if board is Personal
  const {
    data: entData,
    loading: loadingEntData,
  } = useCanDeleteBoardEnterpriseQuery({
    variables: { entId: boardData?.board?.idEnterprise || '' },
    skip: !!boardData?.board?.idOrganization || !boardData?.board?.idEnterprise,
  });

  if (loadingBoardData || loadingOrgData || loadingEntData) {
    return {
      loading: true,
      result: false,
    };
  }

  // Board members query is filtered for admins
  const isBoardAdmin = boardData?.board?.members?.some(
    (member) => member.id === memberId,
  );
  const boardPermissionLevel = boardData?.board?.prefs
    ?.permissionLevel as Board_Prefs_PermissionLevel;

  // Personal board logic
  if (!boardData?.board?.idOrganization) {
    let result;

    if (
      boardData?.board?.idEnterprise &&
      entData?.enterprise?.isRealEnterprise
    ) {
      const isEntAdmin = Boolean(
        entData?.enterprise?.idAdmins?.includes(memberId || ''),
      );
      const entDeleteRestrictionForBoard =
        boardPermissionLevel &&
        entData?.enterprise?.organizationPrefs?.boardDeleteRestrict?.[
          boardPermissionLevel
        ];

      result =
        (isBoardAdmin || isEntAdmin) &&
        (!entDeleteRestrictionForBoard ||
          entDeleteRestrictionForBoard === 'org' ||
          (entDeleteRestrictionForBoard === 'admin' && isEntAdmin));
    } else {
      result = isBoardAdmin;
    }

    return {
      loading: false,
      result,
    };
  }

  // Org members query is filtered for admins
  const isOrgAdmin = orgData?.organization?.members?.some(
    (member) => member.id === memberId,
  );
  const isPaidOrgAdmin =
    isOrgAdmin &&
    ProductFeatures.hasProduct(orgData?.organization?.products?.[0]);
  const orgDeleteRestrictionForBoard =
    boardPermissionLevel &&
    orgData?.organization?.prefs?.boardDeleteRestrict?.[boardPermissionLevel];

  return {
    loading: false,
    result:
      (isBoardAdmin || isPaidOrgAdmin) &&
      (!orgDeleteRestrictionForBoard ||
        orgDeleteRestrictionForBoard === 'org' ||
        (orgDeleteRestrictionForBoard === 'admin' && isOrgAdmin)),
  };
};
